import styled from 'styled-components';

type ColorVariant = 'profit' | 'loss' | 'neutral';

const variantColor = (v: ColorVariant) => {
  if (v === 'profit') return '#5f5';
  if (v === 'loss') return '#f55';
  return '#7dd3fc';
};

const variantShadow = (v: ColorVariant) => {
  if (v === 'profit') return '0 0 30px rgba(80, 255, 120, 0.5)';
  if (v === 'loss') return '0 0 30px rgba(255, 80, 80, 0.5)';
  return '0 0 20px rgba(125, 211, 252, 0.4)';
};

export const Wallet = styled.div`
  flex: 1;
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 10px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 8px;
  }
`;

export const WalletLink = styled.a`
  color: #00d4ff;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #0099ff;
    text-decoration: underline;
  }
`;

export const MainRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);

  @media (max-height: 500px) and (orientation: landscape) {
    padding-bottom: 6px;
    margin-bottom: 6px;
  }
`;

export const AmountSection = styled.div`
  flex: 1;
  text-align: center;
  padding-right: 16px;
  border-right: 1px solid rgba(255, 255, 255, 0.25);

  @media (max-height: 500px) and (orientation: landscape) {
    padding-right: 10px;
  }
`;

export const Label = styled.label`
  display: block;
  font-family: 'Share Tech Mono', monospace;
  font-size: 9px;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  margin-bottom: 4px;

  &:empty {
    display: none;
  }

  @media (max-width: 768px) {
    font-size: 8px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 6px;
    margin-bottom: 2px;
  }
`;

export const Amount = styled.div<{ $variant?: ColorVariant }>`
  font-family: 'Rajdhani', sans-serif;
  font-size: 32px;
  font-weight: 600;
  color: ${({ $variant }) => ($variant ? variantColor($variant) : '#fff')};
  text-shadow: ${({ $variant }) => ($variant ? variantShadow($variant) : 'none')};

  @media (max-width: 768px) {
    font-size: 24px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 19px;
  }
`;

export const PnlSection = styled.div`
  flex: 1;
  text-align: center;
  padding-left: 16px;

  @media (max-height: 500px) and (orientation: landscape) {
    padding-left: 10px;
  }
`;

const statVariantColor = (v: ColorVariant) => {
  if (v === 'profit') return '#5f5';
  if (v === 'loss') return '#f55';
  return 'rgba(255, 255, 255, 0.7)'; // neutral — matches original .detail-stat-value.neutral
};

const roiVariantColor = (v: ColorVariant) => {
  if (v === 'profit') return '#5f5';
  if (v === 'loss') return '#f55';
  return 'rgba(255, 255, 255, 0.5)'; // neutral — matches original .detail-roi-value.neutral
};

export const StatValue = styled.div<{ $type: ColorVariant }>`
  font-family: 'Rajdhani', sans-serif;
  font-size: 22px;
  font-weight: 600;
  color: ${({ $type }) => statVariantColor($type)};

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 14px;
  }
`;

export const ArchetypeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
`;

export const ArchetypeValue = styled.div`
  font-family: 'Rajdhani', sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 204, 255, 0.95);
  text-shadow: 0 0 10px rgba(0, 204, 255, 0.4);
`;

export const RoiSection = styled.div`
  text-align: center;

  label {
    font-size: 11px;
    letter-spacing: 2px;
    margin-bottom: 8px;

    @media (max-width: 768px) {
      font-size: 9px;
    }

    @media (max-height: 500px) and (orientation: landscape) {
      font-size: 7px;
      margin-bottom: 2px;
    }
  }
`;

export const RoiValue = styled.div<{ $type: ColorVariant }>`
  font-family: 'Rajdhani', sans-serif;
  font-size: 35px;
  font-weight: 600;
  color: ${({ $type }) => roiVariantColor($type)};
  text-shadow: ${({ $type }) =>
    $type === 'profit'
      ? '0 0 20px rgba(80, 255, 120, 0.4)'
      : $type === 'loss'
        ? '0 0 20px rgba(255, 80, 80, 0.4)'
        : 'none'};

  @media (max-width: 768px) {
    font-size: 26px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 18px;
  }
`;

export const FollowAction = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;

  @media (max-height: 500px) and (orientation: landscape) {
    margin-top: 8px;
  }
`;

type FollowVariant = 'follow' | 'unfollow';

const followVariantBorder = (v: FollowVariant) =>
  v === 'follow' ? 'rgba(80, 255, 120, 0.6)' : 'rgba(255, 80, 80, 0.6)';
const followVariantColor = (v: FollowVariant) => (v === 'follow' ? '#5f5' : '#f55');
const followVariantGlow = (v: FollowVariant) =>
  v === 'follow' ? 'rgba(80, 255, 120, 0.25)' : 'rgba(255, 80, 80, 0.25)';

export const FollowButton = styled.button<{ $variant: FollowVariant }>`
  font-family: 'Share Tech Mono', monospace;
  font-size: 13px;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding: 10px 28px;
  background: rgba(0, 0, 0, 0.4);
  color: ${({ $variant }) => followVariantColor($variant)};
  border: 1px solid ${({ $variant }) => followVariantBorder($variant)};
  border-radius: 2px;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.05s ease;

  &:hover:not(:disabled) {
    background: ${({ $variant }) =>
      $variant === 'follow' ? 'rgba(80, 255, 120, 0.12)' : 'rgba(255, 80, 80, 0.12)'};
    box-shadow: 0 0 14px ${({ $variant }) => followVariantGlow($variant)};
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 8px 22px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 10px;
    padding: 6px 18px;
  }
`;
